import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sparkles, Menu, X } from 'lucide-react';
import { WebsiteSettings } from '@/lib/firebase';
import { useState } from 'react';

interface HeaderProps {
  settings: WebsiteSettings | null;
}

const Header = ({ settings }: HeaderProps) => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <>
      {/* شريط المعلومات أعلى الهيدر */}
      {settings?.clinic?.workingHours?.[0] && !isMobileMenuOpen && (
        <div id="top-info-bar" className="fixed top-0 left-0 right-0 z-50 bg-gray-100 border-b text-gray-700 py-2 px-4 flex items-center justify-between text-xs sm:text-sm">
                <div className="flex items-center gap-2">
            <span className="inline-block"><svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3" /></svg></span>
                  <span>{settings.clinic.workingHours[0]}</span>
              </div>
              {settings.clinic.phone?.[0] && (
                <a 
                  href={`tel:${settings.clinic.phone[0]}`}
              className="flex items-center gap-1 hover:underline hover:text-blue-600 transition-colors"
                >
              <span className="inline-block"><svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 5h2l.4 2M7 13h10l4-8H5.4M7 13l-1.35 2.7A2 2 0 0 0 7.48 19h9.04a2 2 0 0 0 1.83-1.3L21 13M7 13V6h13" /></svg></span>
              <span dir="ltr">{settings.clinic.phone[0]}</span>
                </a>
              )}
        </div>
      )}

      <header className="bg-white shadow-sm fixed left-0 right-0 z-40" style={{top: settings?.clinic?.workingHours?.[0] && !isMobileMenuOpen ? '40px' : '0'}}>
        <div className="container mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-4 group cursor-pointer">
                <div className={`relative bg-white rounded-2xl shadow-md overflow-hidden border border-gray-100 flex items-center justify-center transition-all duration-300 group-hover:scale-105 ${isHomePage ? 'w-12 h-12' : 'w-16 h-16'}`}>
                  {settings?.clinic.logo ? (
                <img 
                      src={settings.clinic.logo} 
                      alt={settings.clinic.name || "شعار العيادة"} 
                      className="w-full h-full object-contain p-2"
                />
                  ) : (
                    <span className="text-gray-400 text-lg">لا يوجد شعار</span>
                  )}
              </div>
                <span className={`font-extrabold drop-shadow select-none ${isHomePage ? 'text-xl md:text-2xl text-blue-800' : 'text-2xl md:text-3xl text-blue-900'}`}>
                  {settings?.clinic.name || "اسم العيادة"}
                </span>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6 lg:gap-8 animate-fade-in" style={{ animationDelay: '0.2s' }}>
              {isHomePage ? (
                <>
                  <a href="#services" className="text-gray-700 hover:text-blue-600 transition-all duration-300 hover:scale-110 font-medium">
                    الخدمات
                  </a>
                  <a href="#about" className="text-gray-700 hover:text-blue-600 transition-all duration-300 hover:scale-110 font-medium">
                    عن الدكتور
                  </a>
                  <Link to="/cases" className="text-gray-700 hover:text-blue-600 transition-all duration-300 hover:scale-110 font-medium">
                    الحالات
                  </Link>
                  <Link to="/blog" className="text-gray-700 hover:text-blue-600 transition-all duration-300 hover:scale-110 font-medium">
                    المدونة
                  </Link>
                  <a href="#contact" className="text-gray-700 hover:text-blue-600 transition-all duration-300 hover:scale-110 font-medium">
                    التواصل
                  </a>
                </>
              ) : (
                <>
                  <Link to="/#services" className="text-gray-700 hover:text-blue-600 transition-all duration-300 hover:scale-110 font-medium">
                    الخدمات
                  </Link>
                  <Link to="/#about" className="text-gray-700 hover:text-blue-600 transition-all duration-300 hover:scale-110 font-medium">
                    عن الدكتور
                  </Link>
                  <Link to="/cases" className="text-gray-700 hover:text-blue-600 transition-all duration-300 hover:scale-110 font-medium">
                    الحالات
                  </Link>
                  <Link to="/blog" className="text-gray-700 hover:text-blue-600 transition-all duration-300 hover:scale-110 font-medium">
                    المدونة
                  </Link>
                  <Link to="/#contact" className="text-gray-700 hover:text-blue-600 transition-all duration-300 hover:scale-110 font-medium">
                    التواصل
                  </Link>
                </>
              )}
              <Link to="/booking">
                <Button className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 bg-primary hover:bg-primary/90 h-10 bg-gradient-to-r from-blue-600 via-purple-600 to-green-600 hover:from-blue-700 hover:via-purple-700 hover:to-green-700 text-white px-6 py-2 rounded-full font-semibold shadow-xl transition-all duration-300 hover:scale-105 hover:shadow-2xl">
                  <Sparkles className="ml-2 w-4 h-4" />
                  احجز موعد
                </Button>
              </Link>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="فتح القائمة"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-gray-700" />
              ) : (
                <Menu className="w-6 h-6 text-gray-700" />
              )}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMobileMenuOpen && (
            <nav className="md:hidden mt-4 py-4 border-t border-gray-100 animate-fade-in fixed top-0 right-0 left-0 z-50 bg-white shadow-lg" style={{marginTop: 0}}>
              <div className="flex justify-end px-4 mb-2">
                <button onClick={() => setIsMobileMenuOpen(false)} aria-label="إغلاق القائمة" className="p-2 rounded-lg hover:bg-gray-100">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="flex flex-col space-y-4">
                {isHomePage ? (
                  <>
                    <a href="#services" className="text-gray-700 hover:text-blue-600 transition-all duration-300 font-medium px-4 py-2 hover:bg-gray-50 rounded-lg">الخدمات</a>
                    <a href="#about" className="text-gray-700 hover:text-blue-600 transition-all duration-300 font-medium px-4 py-2 hover:bg-gray-50 rounded-lg">عن الدكتور</a>
                    <Link to="/cases" className="text-gray-700 hover:text-blue-600 transition-all duration-300 font-medium px-4 py-2 hover:bg-gray-50 rounded-lg">الحالات</Link>
                    <Link to="/blog" className="text-gray-700 hover:text-blue-600 transition-all duration-300 font-medium px-4 py-2 hover:bg-gray-50 rounded-lg">المدونة</Link>
                    <a href="#contact" className="text-gray-700 hover:text-blue-600 transition-all duration-300 font-medium px-4 py-2 hover:bg-gray-50 rounded-lg">التواصل</a>
                  </>
                ) : (
                  <>
                    <Link to="/#services" className="text-gray-700 hover:text-blue-600 transition-all duration-300 font-medium px-4 py-2 hover:bg-gray-50 rounded-lg">الخدمات</Link>
                    <Link to="/#about" className="text-gray-700 hover:text-blue-600 transition-all duration-300 font-medium px-4 py-2 hover:bg-gray-50 rounded-lg">عن الدكتور</Link>
                    <Link to="/cases" className="text-gray-700 hover:text-blue-600 transition-all duration-300 font-medium px-4 py-2 hover:bg-gray-50 rounded-lg">الحالات</Link>
                    <Link to="/blog" className="text-gray-700 hover:text-blue-600 transition-all duration-300 font-medium px-4 py-2 hover:bg-gray-50 rounded-lg">المدونة</Link>
                    <Link to="/#contact" className="text-gray-700 hover:text-blue-600 transition-all duration-300 font-medium px-4 py-2 hover:bg-gray-50 rounded-lg">التواصل</Link>
                  </>
                )}
                <Link to="/booking" className="w-full">
                  <Button className="w-full bg-gradient-to-r from-blue-600 via-purple-600 to-green-600 hover:from-blue-700 hover:via-purple-700 hover:to-green-700 text-white py-2 rounded-lg font-semibold shadow-xl transition-all duration-300">
                    <Sparkles className="ml-2 w-4 h-4" />
                    احجز موعد
                  </Button>
                </Link>
              </div>
            </nav>
          )}
        </div>
      </header>
    </>
  );
};

export default Header; 